import { queryClient } from '@app/core/integrations/react-query/react-query';
import { useListViewContext } from '@app/core/list-view/context/ListViewContext';
import { auditReports } from '@mi-front/api-clients';
import { useEffect, useRef } from 'react';

interface UseAutoRefreshRunsProps {
  runs: auditReports.AodRunInGetManyDto[] | undefined;
  intervalMs?: number;
}

/**
 * Hook that automatically refreshes the runs list when there are runs in progress.
 * Polls every 15 seconds (or custom interval) if any run has status IN_PROGRESS, WAITING, or WAITING_FOR_REPROCESS.
 * Uses ListView context listId and queryClient.invalidateQueries to refetch. Must be used within ListView.
 */
export function useAutoRefreshRuns({ runs, intervalMs = 15000 }: UseAutoRefreshRunsProps) {
  const { listId } = useListViewContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const hasInProgressRuns = () => {
      if (!runs || runs.length === 0) return false;

      return runs.some(
        (run: auditReports.AodRunInGetManyDto) =>
          run.status === auditReports.AodRunInGetManyDtoStatusEnum.IN_PROGRESS ||
          run.status === auditReports.AodRunInGetManyDtoStatusEnum.WAITING ||
          run.status === auditReports.AodRunInGetManyDtoStatusEnum.WAITING_FOR_REPROCESS,
      );
    };

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (hasInProgressRuns()) {
      intervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: [listId] });
      }, intervalMs);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runs, listId, intervalMs]);
}
